import { useQuery } from '@tanstack/react-query';

import { MFAAPI, type MFAStatusResponse } from '../lib/api/mfa';
import { queryKeys } from '../lib/query/client';
import { useAuthContext } from './useAuthContext';

/**
 * Fetches the current user's MFA status. Used by the route guard, the
 * settings panel, and the enrollment wizard step to decide what to render.
 *
 * Stale-time of 60s keeps the query cheap during navigation while still
 * picking up status changes after the user finishes the enrollment wizard
 * (the verify-enrollment mutation invalidates this key explicitly).
 */
export function useMFAStatus() {
  const { isAuthenticated } = useAuthContext();
  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.mfaStatus(),
    queryFn: ({ signal }) => MFAAPI.status(signal),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  const status: MFAStatusResponse | undefined = data;

  return {
    status,
    isLoading,
    enrolled: status?.enrolled ?? false,
    enforcementActive: status?.enforcementActive ?? true,
    recoveryCodesRemaining: status?.recoveryCodesRemaining ?? 0,
    refetch,
  };
}
